import { api as base } from "utils/api";
import type {
  PassType,
  RealtimeRevenueData,
  RealtimeMembersData,
  SalesSummaryData,
  SalesDetailData,
  TargetDataResponse,
  TargetDataItem,
  MemberSummaryData,
  MemberDetailData,
} from "types/BusinessDashboard";

const PATH = "/dashboard";

// =================================================================
// 1. 실시간 현황 API
// =================================================================

export interface RealtimeParams {
  referenceDate: string; // YYYY-MM-DD
  seqPartner?: number;
  seqPartnerBranch?: number;
}

export const getRealtimeRevenue = async (
  params: RealtimeParams
): Promise<RealtimeRevenueData> => {
  const { data } = await base.get<RealtimeRevenueData>(
    `${PATH}/revenue/real-time`,
    { params }
  );
  return data;
};

export const getRealtimeActiveMembers = async (
  params: RealtimeParams
): Promise<RealtimeMembersData> => {
  const { data } = await base.get<RealtimeMembersData>(
    `${PATH}/active-member/real-time`,
    { params }
  );
  return data;
};

// =================================================================
// 2. 매출 분석 API
// =================================================================

export interface MonthlyParams {
  referenceMonth: string; // YYYY-MM
  seqPartner?: number;
  seqPartnerBranch?: number;
}

export const getSalesSummary = async (
  params: MonthlyParams
): Promise<SalesSummaryData> => {
  const { data } = await base.get<SalesSummaryData>(
    `${PATH}/revenue/monthly/summary`,
    { params }
  );
  return data;
};

export interface SalesDetailParams extends MonthlyParams {
  passType: PassType;
}

export const getSalesDetail = async (
  params: SalesDetailParams
): Promise<SalesDetailData> => {
  const { data } = await base.get<SalesDetailData>(
    `${PATH}/revenue/monthly/detail`,
    { params }
  );
  return data;
};

// BEP & 목표 매출 관련 API
export const getTargetData = async (
  params: MonthlyParams
): Promise<TargetDataResponse> => {
  const { data } = await base.get<TargetDataResponse>(
    `${PATH}/revenue/target-data`,
    { params }
  );
  return data;
};

export interface PostTargetDataPayload {
  referenceMonth: string;
  seqPartnerBranch?: number;
  targetData: TargetDataItem[];
}

export const postTargetData = async (
  payload: PostTargetDataPayload
): Promise<void> => {
  await base.post(`${PATH}/revenue/target-data`, payload);
};

export const patchTargetData = async (
  payload: PostTargetDataPayload
): Promise<void> => {
  await base.patch(`${PATH}/revenue/target-data`, payload);
};

// =================================================================
// 3. 회원 분석 API
// =================================================================

export interface MemberMonthlyParams {
  referenceStartMonth: string; // YYYY-MM
  referenceEndMonth: string; // YYYY-MM
  seqPartner?: number;
  seqPartnerBranch?: number;
}

export interface MemberSummaryParams extends MemberMonthlyParams {
  passType: PassType;
}

export const getMemberSummary = async (
  params: MemberSummaryParams
): Promise<MemberSummaryData> => {
  const { data } = await base.get<MemberSummaryData>(
    `${PATH}/active-member/monthly`,
    { params }
  );
  return data;
};

export const getMemberDetail = async (
  params: MemberMonthlyParams
): Promise<MemberDetailData> => {
  const { data } = await base.get<MemberDetailData>(
    `${PATH}/active-member/monthly/detail`,
    { params }
  );
  return data;
};
